'use client'
import { useState, useEffect } from 'react'
import { useFormspree } from '@/hooks/useFormspree'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import type { Job } from '@/lib/airtable'
import { trackEvent } from '@/lib/posthog'
import { FORMSPREE_FORM_IDS, POSTHOG_EVENTS } from '@/lib/constants'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^\d{10}$/
const CDL_RE = /^[A-Za-z0-9]{4,20}$/
const FALLBACK_JOBS: Job[] = [{ id: 'cdl-driver', title: 'CDL Driver' }]

interface FieldErrors {
  name?: string
  email?: string
  phone?: string
  cdl_number?: string
  cdl_state?: string
  years_experience?: string
  position?: string
}

export function ApplicationForm() {
  const { status, error, submit } = useFormspree(FORMSPREE_FORM_IDS.APPLICATIONS)
  const { ref: cdlRef, isVisible: cdlVisible } = useIntersectionObserver({ threshold: 0.6 })
  const [fields, setFields] = useState({
    name: '',
    email: '',
    phone: '',
    cdl_number: '',
    cdl_state: '',
    years_experience: '',
    position: '',
    tcpaConsent: false,
  })
  const [errors, setErrors] = useState<FieldErrors>({})
  const [jobs, setJobs] = useState<Job[]>([])
  const [jobsStatus, setJobsStatus] = useState<'loading' | 'loaded' | 'error'>('loading')

  useEffect(() => {
    fetch('/api/jobs')
      .then(r => r.json())
      .then(data => {
        setJobs(data.jobs ?? [])
        setJobsStatus('loaded')
      })
      .catch(() => setJobsStatus('error'))
  }, [])

  const displayJobs = jobsStatus === 'error' || jobs.length === 0 ? FALLBACK_JOBS : jobs

  function set(key: string, value: string) {
    setFields(prev => ({ ...prev, [key]: value }))
  }

  function validate(): boolean {
    const errs: FieldErrors = {}
    if (!fields.name.trim()) errs.name = 'Name is required'
    if (!EMAIL_RE.test(fields.email)) errs.email = 'Enter a valid email address'
    if (!PHONE_RE.test(fields.phone.replace(/\D/g, '')))
      errs.phone = 'Enter a 10-digit phone number'
    if (cdlVisible) {
      if (!CDL_RE.test(fields.cdl_number))
        errs.cdl_number = 'Enter your CDL number as shown on your license'
      if (!fields.cdl_state.trim()) errs.cdl_state = 'CDL state is required'
      if (!fields.years_experience || Number(fields.years_experience) < 0)
        errs.years_experience = 'Years of experience is required'
    }
    if (jobsStatus !== 'loading' && !fields.position)
      errs.position = 'Position is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    submit({ ...fields })
    trackEvent(POSTHOG_EVENTS.JOB_APPLY_SUBMIT, {
      position: fields.position,
      cdl_provided: fields.cdl_number.length > 0,
    })
  }

  if (status === 'success') {
    return (
      <div className="glass-card rounded-2xl p-10 text-center">
        <p className="text-white text-xl font-semibold">Application received!</p>
        <p className="text-[color:var(--foreground-muted)] mt-2 text-sm">
          Our team will review your application and reach out within 2 business days.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Driver application form"
      noValidate
      className="glass-card rounded-2xl p-8 flex flex-col gap-5"
    >
      <div aria-live="polite" aria-atomic="true" className="min-h-[1.25rem]">
        {error && <p className="text-red-400 text-sm font-medium">{error}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="app-name" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Your Name <span aria-hidden="true">*</span>
        </label>
        <input
          id="app-name"
          type="text"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.name ? ' border border-red-500' : ''}`}
          value={fields.name}
          onChange={e => set('name', e.target.value)}
          autoComplete="name"
        />
        {errors.name && <p className="text-red-400 text-xs">{errors.name}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="app-email" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Email <span aria-hidden="true">*</span>
        </label>
        <input
          id="app-email"
          type="email"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.email ? ' border border-red-500' : ''}`}
          value={fields.email}
          onChange={e => set('email', e.target.value)}
          autoComplete="email"
        />
        {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="app-phone" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Phone <span aria-hidden="true">*</span>
        </label>
        <input
          id="app-phone"
          type="tel"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.phone ? ' border border-red-500' : ''}`}
          value={fields.phone}
          onChange={e => set('phone', e.target.value)}
          autoComplete="tel"
          placeholder="10-digit number"
        />
        {errors.phone && <p className="text-red-400 text-xs">{errors.phone}</p>}
        <p className="text-xs text-gray-500 max-w-prose">
          By providing your phone number, you agree to receive SMS messages from ADG2 Transport Corp for service updates and communication. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out, HELP for assistance.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="app-position" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Position <span aria-hidden="true">*</span>
        </label>
        {jobsStatus === 'loading' ? (
          <div className="glass-input rounded-lg h-12 animate-pulse" aria-busy="true" />
        ) : (
          <select
            id="app-position"
            className={`glass-input rounded-lg px-4 py-3 text-white w-full${errors.position ? ' border border-red-500' : ''}`}
            value={fields.position}
            onChange={e => set('position', e.target.value)}
          >
            <option value="">Select a position…</option>
            {displayJobs.map(job => (
              <option key={job.id} value={job.title}>{job.title}</option>
            ))}
          </select>
        )}
        {errors.position && <p className="text-red-400 text-xs">{errors.position}</p>}
      </div>

      <div ref={cdlRef as React.RefCallback<HTMLDivElement>} className="flex flex-col gap-5">
        {cdlVisible && (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor="app-cdl_number" className="text-sm font-medium text-[color:var(--foreground-muted)]">
                CDL Number <span aria-hidden="true">*</span>
              </label>
              <input
                id="app-cdl_number"
                type="text"
                className={`glass-input rounded-lg px-4 py-3 text-white${errors.cdl_number ? ' border border-red-500' : ''}`}
                value={fields.cdl_number}
                onChange={e => set('cdl_number', e.target.value)}
                placeholder="e.g. A12345678"
              />
              {errors.cdl_number && <p className="text-red-400 text-xs">{errors.cdl_number}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1">
                <label htmlFor="app-cdl_state" className="text-sm font-medium text-[color:var(--foreground-muted)]">
                  CDL State <span aria-hidden="true">*</span>
                </label>
                <input
                  id="app-cdl_state"
                  type="text"
                  className={`glass-input rounded-lg px-4 py-3 text-white${errors.cdl_state ? ' border border-red-500' : ''}`}
                  value={fields.cdl_state}
                  onChange={e => set('cdl_state', e.target.value)}
                  placeholder="e.g. NJ"
                  maxLength={2}
                />
                {errors.cdl_state && <p className="text-red-400 text-xs">{errors.cdl_state}</p>}
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="app-years_experience" className="text-sm font-medium text-[color:var(--foreground-muted)]">
                  Years of Experience <span aria-hidden="true">*</span>
                </label>
                <input
                  id="app-years_experience"
                  type="number"
                  min="0"
                  className={`glass-input rounded-lg px-4 py-3 text-white${errors.years_experience ? ' border border-red-500' : ''}`}
                  value={fields.years_experience}
                  onChange={e => set('years_experience', e.target.value)}
                />
                {errors.years_experience && <p className="text-red-400 text-xs">{errors.years_experience}</p>}
              </div>
            </div>
          </>
        )}
      </div>

      <label className="flex items-start gap-3 text-sm text-[color:var(--foreground-muted)] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[color:var(--accent)] flex-shrink-0"
          checked={fields.tcpaConsent}
          onChange={e => setFields(prev => ({ ...prev, tcpaConsent: e.target.checked }))}
          aria-required="true"
        />
        <span>
          I agree to receive transactional SMS messages from ADG2 Transport Corp regarding my application. Message and data rates may apply. Reply STOP to opt out.{' '}
          <a href="/privacy-policy" className="underline hover:text-white transition-colors">Privacy Policy</a>
          {' '}·{' '}
          <a href="/terms-of-service" className="underline hover:text-white transition-colors">Terms</a>
        </span>
      </label>

      <button
        type="submit"
        disabled={status === 'loading' || !fields.tcpaConsent}
        className="glass-button rounded-xl px-8 py-4 font-semibold text-base mt-2 disabled:opacity-50"
      >
        {status === 'loading' ? 'Sending…' : 'Apply Now'}
      </button>
    </form>
  )
}
